<template>
  <div>
    <booking-alerts
      :booking="booking"/>

    <booking-thread-list
      v-if="!booking.is_offer && booking.threads.length && !$userIsIntern"
      :threads="booking.threads"
      :booking="booking"/>

    <booking-talent-list
      v-if="!booking.is_offer &&  ($userIsNotTalent || showAllTalents)"
      :showAllTalents="!$userIsNotTalent && showAllTalents"
      :booking="booking"/>

    <booking-details
      :booking="booking"
      :property-mutator="setBookingProperty"/>

    <div class="dashboard-block button-bank" v-if="canViewRegionalAdminOptions || !$userIsAdmin">

      <router-link
        v-if="$userIsAdmin"
        :to="{ name: 'bookings.edit', params: { id: booking.id } }"
        class="btn btn-info">
        <i class="fa fa-fw fa-pencil"></i> Edit Booking
      </router-link>

      <router-link
        v-if="$userIsAdmin || $userIsClient"
        :to="{ name: 'bookings.duplicate', params: { id: booking.id } }"
        class="btn btn-info">
        <i class="fa fa-fw fa-clone"></i> Duplicate Booking
      </router-link>

      <ladda-button
        v-if="userCanReactivateBooking"
        class="btn btn-info"
        :click="handleReactivateBookingCallback">
        <i class="fa fa-fw fa-check"></i> Reactivate Booking
      </ladda-button>

      <ladda-button
        v-if="userCanMarkAsBookedUnpaid"
        class="btn btn-danger"
        :click="handleMarkBookingBookedUnpaid">
        <i class="fa fa-fw fa-close"></i> Mark as Booked but Unpaid
      </ladda-button>

      <ladda-button
        v-if="userCanMarkCompletedAsIncomplete"
        class="btn btn-info"
        :click="handleMarkBookingCompletedIncomplete">
        <i class="fa fa-ban"></i> Mark Incomplete
      </ladda-button>

      <a href="#" data-toggle="modal" data-target="#replace-talent" @click="toggleGetTalent" :disabled="bookingIsCompleted" v-if="!this.booking.is_offer">
        <button
          v-if="userCanReplaceTalent"
          class="btn btn-info"
          :disabled="bookingIsCompleted">
          <i class="fa fa-fw fa-refresh"></i> Replace {{ $starLabel | sentenceCase }}
        </button>
      </a>

      <ladda-button
        v-if="userCanCancelBooking"
        class="btn btn-danger"
        :click="handleCancelBookingCallback">
        <i class="fa fa-fw fa-close"></i> Cancel Booking
      </ladda-button>

      <ladda-button
        v-if="userCanRefundBooking"
        class="btn btn-danger"
        :click="handleRefundBookingCallback">
        <i class="fa fa-fw fa-close"></i> Refund Booking
      </ladda-button>

      <button class="btn btn-default"
              v-if="cancellationRequested"
              disabled>
        <i class="fa fa-fw fa-close"></i> Cancellation requested
      </button>

      <ladda-button
        v-if="$userIsAdmin && bookingIsOnHold"
        class="btn btn-info"
        :click="handleRestoreBookingCallback">
        <i class="fa fa-fw fa-undo"></i> Restore Booking
      </ladda-button>

      <ladda-button
        v-if="$chatsModuleEnabled && userCanCreateBookingThreads && !booking.is_offer"
        class="btn btn-info"
        :click="handleCreateBookingThreads">
        <i class="fa fa-fw fa-commenting"></i> Create Chats
      </ladda-button>

      <ladda-button
        v-if="booking.package_id && this.$userIsAdmin"
        class="btn btn-info"
        :click="handleMarkBookingCompleteCallback">
        <i class="fa fa-fw fa-check"></i> Complete Booking
      </ladda-button>
    </div>

    <booking-client-details
      v-if="($userIsAdmin || $userIsClient) || (($userIsTalent || $userIsManager) && $showClientDetailsBookingModuleEnabled)"
      :booking="booking"/>

    <booking-notes
        v-if="$userIsAdmin"
        :booking="booking"/>

    <booking-sales-pipeline key="1" v-if="$userIsAdmin && !$vlastMode" :booking="booking"/>

    <booking-admin-options
      v-if="canViewRegionalAdminOptions"
      :booking="booking"/>

    <booking-source
      v-if="$userIsAdmin && !$vlastMode && canViewRegionalAdminOptions"
      :booking="booking"/>

    <booking-download-pdf
      v-if="$userIsAdmin"
      :booking="booking"/>

    <booking-upload-files
      v-if="!$userIsIntern && !booking.package_id"
      :booking="booking"/>

    <booking-upload-video v-if="isShoutout && (bookingIsBooked || bookingIsCompleted) && ( $userIsAdmin || $userIsManager )" :booking="booking" :talent="booking.talent[0]" />

    <div class="dashboard-block" v-if="!$vlastMode" >
      <div class="alert alert-icon alert-info" >
        <i class="fa fa-list"/>
        Read our <a :href="bookingTncLink()" target="_blank">terms and conditions</a> here
      </div>
    </div>

    <booking-replace-talent-modal
        :get-talent="this.getTalent"
        v-if="$userIsAdmin"/>

    <booking-cancel-reason-modal
      ref="modal"
      @input="handleCancelBookingReason"/>

  </div>
</template>

<script>
import { helpers } from 'mixins/bookings'
import { mapState, mapActions } from 'vuex'
import { alert, notify } from 'utils'
import BookingDetails from './BookingDetails'
import BookingClientDetails from './BookingClientDetails'
import BookingAdminOptions from './BookingAdminOptions'
import BookingTalentList from './BookingTalentList'
import BookingDownloadPdf from './BookingDownloadPdf'
import BookingNotes from 'components/booking/BookingNotes'
import BookingSource from 'components/booking/BookingSource'
import BookingReplaceTalentModal from './BookingReplaceTalentModal'
import BookingThreadList from 'components/booking/BookingThreadList'
import BookingClientCancelReasonModal from './BookingClientCancelReasonModal'
import BookingUploadFiles from './BookingUploadFiles'
import BookingUploadVideo from './BookingUploadVideo'
import BookingAlerts from 'components/booking/BookingAlerts'
import CampaignNotification from '../campaign/CampaignNotification'
import BookingCancelReasonModal from './BookingCancelReasonModal'
import { OpportunityTracker } from 'mixins/analytics'
import BookingTnc from 'mixins/tnc'

import $ from 'jquery'
import BookingSalesPipeline from './BookingSalesPipeline'

export default {
  mixins: [helpers, OpportunityTracker, BookingTnc],

  data () {
    return {
      getTalent: false,
      cancelBookingReason: null,
      cancelBookingPromise: null
    }
  },

  computed: {
    ...mapState('booking', ['booking']),

    showAllTalents () {
      return this.booking.show_all_talents && this.booking.talentAttending && this.booking.talentAttending.length
    },
    userCanCancelBooking () {
      if (this.bookingIsCompleted) {
        return false
      }

      if (this.$userIsAdmin) {
        return this.bookingIsNotCancelled
      } else if (this.$userIsTalent) {
        return this.bookingIsNotCancelled &&
          this.bookingAttendingTalent.every(talent => talent.response.status !== 'cancelled') &&
          !this.booking.is_offer
      }

      return false
    },

    userCanRefundBooking () {
      return (this.booking.package_id && this.$userIsAdmin && !this.bookingIsCompleted && !this.bookingIsCancelled)
    },

    userCanCreateBookingThreads () {
      return (this.$userIsAdmin && (!this.booking.threads || !this.booking.threads.length))
    },

    userCanMarkAsBookedUnpaid () {
      return (this.$paymentsModuleEnabled && this.bookingIsScheduled && this.$userIsAdmin)
    },

    userCanMarkCompletedAsIncomplete () {
      return (this.bookingIsCompleted && this.$userIsAdmin)
    },

    userCanReactivateBooking () {
      return (this.bookingIsCancelled && this.$userIsAdmin)
    },

    userCanReplaceTalent () {
      return this.$userIsAdmin
    },

    cancellationRequested () {
      if (this.$userIsClient) {
        return this.bookingIsCancelled || this.bookingIsOnHold
      }

      return false
    },

    canViewRegionalAdminOptions () {
      return this.$userIsBookingRegionalAdmin(this.$user, this.booking)
    },

    isShoutout () {
      return this.booking.package_id === 1
    }
  },

  components: {
    BookingSalesPipeline,
    CampaignNotification,
    BookingUploadFiles,
    BookingClientCancelReasonModal,
    BookingDetails,
    BookingClientDetails,
    BookingAdminOptions,
    BookingTalentList,
    BookingDownloadPdf,
    BookingNotes,
    BookingSource,
    BookingReplaceTalentModal,
    BookingThreadList,
    BookingAlerts,
    BookingUploadVideo,
    BookingCancelReasonModal
  },

  methods: {
    ...mapActions('booking', [
      'getBooking',
      'cancelBooking',
      'restoreBookingOnHold',
      'notifyAdminOfTalentAttemptToCancel',
      'markBookingBookedButUnpaid',
      'markBookingIncomplete',
      'createBookingThreads',
      'refundBooking',
      'markBookingComplete',
      'reactivateBooking',
      'setBookingProperty'
    ]),

    toggleGetTalent () {
      this.getTalent = true
    },

    handleMarkBookingCompleteCallback (type) {
      let payload = {
        id: this.booking.id,
        type: type
      }

      return this.markBookingComplete(payload)
        .then(() => {
          notify.success('Booking has been marked as complete.')
          this.trackEvent(this.analytics.V2.JobBookingCompleteEvent)
          // Get the updated booking resource since the talent's response has changed
          this.getBooking({ id: this.booking.id })
        })
    },

    /**
     * Handle cancelling a booking.
     *
     * @return Promise
     */
    handleCancelBookingCallback () {
      const para = document.createElement('span')
      para.innerHTML = 'Are you sure you want to cancel this booking? You may still be required to pay some or all of' +
        ' your fee. View the terms and conditions ' +
        '<a :href="bookingTncLink()" target="_blank">here</a> or call 1300 657 601.'

      const alertOptionsTalent = {
        title: 'Contact ',
        text: `As this event is in the booking stage, you cannot cancel the booking. If you are not able to complete the event, please contact ${this.clientName} on ${this.contactNumber} or ${this.contactEmail} to cancel this booking.`
      }

      if (this.$userIsTalent) {
        return alert.info(alertOptionsTalent).then(() => {
          return this.notifyAdminOfTalentAttemptToCancel({ id: this.booking.id })
        })
      } else {
        return this.handleCancelBookingModalCallback()
      }
    },

    handleRefundBookingCallback () {
      const confirmAlertOptionsAdmin = {
        title: 'Cancel Booking',
        text: 'Are you sure you want to refund this booking?',
        buttons: {
          confirm: {
            text: 'Yes, refund booking.'
          }
        }
      }

      if (this.$userIsAdmin) {
        return alert.confirm(confirmAlertOptionsAdmin)
          .then(() => {
            return this.refundBooking({ id: this.booking.id })
          })
      }
    },

    /**
     * Handle marking an unpaid booking as booked so that talent receive notifications
     *
     * @return Promise
     */
    handleCreateBookingThreads () {
      const confirmAlertOptions = {
        title: 'Create Chats',
        text: 'Are you sure you want to create chats. ' +
          'This will allow talent and clients to communicate.',
        buttons: {
          confirm: {
            text: 'Yes, create chats.'
          }
        }
      }

      return alert.confirm(confirmAlertOptions)
        .then(() => {
          return this.createBookingThreads({ id: this.booking.id })
        })
        .then(() => {
          return alert.info({
            title: 'Chats Created',
            text: 'Chats Created'
          })
        })
    },

    /**
     * Handle reactivating a booking
     *
     * @return Promise
     */
    handleReactivateBookingCallback () {
      const confirmAlertOptions = {
        title: 'Reactivate Booking',
        text: 'Are you sure you want to reactivate this booking?',
        buttons: {
          confirm: {
            text: 'Yes, reactivate.'
          }
        }
      }

      return alert.confirm(confirmAlertOptions)
        .then(() => {
          return this.reactivateBooking({ id: this.booking.id })
        })
        .then(() => {
          return alert.info({
            title: 'Booking Reactivated'
          })
        })
    },

    /**
     * Handle marking an unpaid booking as booked so that talent receive notifications
     *
     * @return Promise
     */
    handleMarkBookingBookedUnpaid () {
      const confirmAlertOptions = {
        title: 'Mark as Booked but Unpaid',
        text: 'Are you sure you want to mark this booking as booked while the deposit is unpaid? ' +
                'This will allow talent to receive all notifications they would normally receive for a booking that has had it\'s deposit paid.',
        buttons: {
          confirm: {
            text: 'Yes, mark as booked.'
          }
        }
      }

      return alert.confirm(confirmAlertOptions)
        .then(() => {
          return this.markBookingBookedButUnpaid({ id: this.booking.id })
        })
        .then(() => {
          return alert.info({
            title: 'Booking Marked as Booked but Unpaid',
            text: 'Booking Marked as Booked but Unpaid.'
          })
        })
    },

    /**
     * Handle marking a completed booking incomplete
     *
     * @return Promise
     */
    handleMarkBookingCompletedIncomplete () {
      const confirmAlertOptions = {
        title: 'Mark as Incomplete',
        text: 'Are you sure you want to mark this booking as incomplete?',
        buttons: {
          confirm: {
            text: 'Yes, mark as incomplete.'
          }
        }
      }

      return alert.confirm(confirmAlertOptions)
        .then(() => {
          return this.markBookingIncomplete({ id: this.booking.id })
        })
        .then(() => {
          notify.success('Booking has been marked as incomplete.')
          // Get the updated booking resource since the talent's response has changed
          this.getBooking({ id: this.booking.id })
        })
    },

    /**
     * Restore a booking that is on hold.
     *
     * @return Promise
     */
    handleRestoreBookingCallback () {
      const confirmAlertOptions = {
        title: 'Restore Booking',
        text: 'Are you sure you want to restore this bookings previous state?',
        buttons: {
          confirm: {
            text: 'Yes, restore booking.'
          }
        },
        dangerMode: false
      }

      return alert.confirm(confirmAlertOptions)
        .then(() => {
          return this.restoreBookingOnHold({ id: this.booking.id })
        })
    },

    /**
     * Handle client cancelling a booking.
     *
     * @return Promise
     */

    handleCancelBookingModalCallback () {
      this.cancelBookingReason = null

      const confirmAlertOptions = {
        title: 'Cancel Booking',
        text: `Are you sure you would like to cancel this ${this.$t('booking')}?`
      }

      return alert.confirm(confirmAlertOptions)
        .then(() => {
          $(this.$refs.modal.$el).modal('show')
        })
    },

    handleCancelBookingReason (reason) {
      this.cancelBookingReason = reason

      const cancelSuccessAlertOptions = {
        title: this.$userIsAdmin ? this.$t('Booking') + ' Cancelled' : this.$t('Booking') + ' On Hold',
        text: this.$userIsAdmin ? this.$t('Booking') + ' has been canceled' : `Before your ${this.$t('booking')} is cancelled ${this.clientName} will be reviewing your request.`,
        buttons: {
          confirm: {
            text: 'Continue'
          }
        },
        timer: 3000
      }

      this.cancelBooking({ id: this.booking.id, reason: this.cancelBookingReason })
        .then(() => {
          return alert.timedSuccess(cancelSuccessAlertOptions)
        })
    },

    /**
     * Handle the cancel booking modal being hidden.
     *
     * @return void
     */
    handleCancelBookingModalHiddenCallback () {
      // If our modal component emitted a cancel reason then we'll resolve the promise as we'll
      // continue with cancelling the booking.
      if (this.cancelBookingReason) {
        this.cancelBookingPromise.resolve()

        // If no reason was emitted then the modal was closed so we'll reject the promise to
        // prevent the cancelling.
      } else {
        this.cancelBookingPromise.reject()
        this.cancelBookingReason = null
      }
    }
  }
}
</script>
